import { Link } from "react-router-dom";
import { FaApple } from "react-icons/fa";
import PublicLayout from "./PublicLayout";
import { MdEvent, MdPeople, MdCalculate, MdReceipt, MdTrendingUp, MdVerifiedUser, MdCheck, MdNotifications, MdOfflineBolt } from "react-icons/md";
import HomeCalendarWidget from "./HomeCalendarWidget";
import MevzuatHaberleriWidget from "./MevzuatHaberleriWidget";
import { MONTHS } from "data/tax-calendar";
import { SITE_TAX_YEAR, SITE_TAX_COUNT, getUpcomingEvents, isTahmini } from "data/tax-calendar-years";

const APP_STORE_URL = "https://apps.apple.com/app/id6768791934";

// Bugünden itibaren yaklaşan 5 tarih (yıl sonunu geçip sonraki yılın verisine devam eder)
function yaklasanVergiler(now: Date) {
  const bugun = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return getUpcomingEvents(now, 5).map(({ year, event }) => {
    const [mm, dd] = event.date.split("-").map(Number);
    const kalanGun = Math.round((new Date(year, mm - 1, dd).getTime() - bugun) / 86400000);
    const tahmini = isTahmini(year);
    return {
      date: `${String(dd).padStart(2, "0")} ${MONTHS[mm - 1].name}${year !== now.getFullYear() ? ` ${year}` : ""}`,
      title: `${event.title}${tahmini ? " (tahmini)" : ""}`,
      color: kalanGun <= 3 ? "red" : kalanGun <= 10 ? "orange" : "green",
    };
  });
}

const Home = () => {
  const yaklasanlar = yaklasanVergiler(new Date());
  return (
    <PublicLayout
      title={`Mali Ajanda - ${SITE_TAX_YEAR} Mali Takvim, Vergi Takvimi ve SMMM Yazılımı`}
      description={`Mali müşavirler ve SMMM'ler için ${SITE_TAX_YEAR} mali takvim, vergi takvimi, cari hesap, çek/senet ve 26 profesyonel hesaplama aracı. ${SITE_TAX_COUNT} vergi tarihi otomatik takip. Ücretsiz başla.`}
    >
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-blue-50 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
            {/* Sol: Hero metni */}
            <div className="lg:col-span-7 lg:text-left text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-100 px-4 py-1.5 text-xs font-bold text-brand-600">
                <span className="h-2 w-2 rounded-full bg-brand-500"></span>
                {isTahmini(SITE_TAX_YEAR) ? `${SITE_TAX_YEAR} Vergi Takvimi (Tahmini)` : `Yeni: ${SITE_TAX_YEAR} Vergi Takvimi Güncel`}
              </div>
              <h1 className="text-4xl font-black leading-tight text-navy-700 sm:text-5xl lg:text-[3.25rem] xl:text-6xl">
                Mali Müşavirler İçin <span className="text-brand-500">{SITE_TAX_YEAR} Mali Takvim</span> ve Dijital Ajanda
              </h1>
              <p className="mt-6 max-w-xl text-lg text-gray-600 lg:max-w-none mx-auto lg:mx-0">
                <strong>Mali Ajanda</strong> — {SITE_TAX_YEAR} vergi takvimi, cari hesap takibi, çek/senet yönetimi ve 26 profesyonel hesaplama aracı tek platformda. {SITE_TAX_COUNT} vergi tarihi otomatik takip, mali müşavirler ve SMMM'ler için özel tasarlandı.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
                <Link to="/auth/sign-in" className="rounded-xl bg-brand-500 px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600">
                  Ücretsiz Başla
                </Link>
                <Link to="/ozellikler" className="rounded-xl border-2 border-gray-200 bg-white px-8 py-4 text-base font-bold text-navy-700 hover:border-brand-500 hover:text-brand-500">
                  Özellikleri İncele
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-500 lg:justify-start">
                <div className="flex items-center gap-1"><MdCheck className="h-4 w-4 text-green-500" /> Kredi kartı gerekmez</div>
                <div className="flex items-center gap-1"><MdCheck className="h-4 w-4 text-green-500" /> Anında kayıt</div>
                <div className="flex items-center gap-1"><MdCheck className="h-4 w-4 text-green-500" /> 443+ vergi tarihi</div>
              </div>
            </div>

            {/* Sağ: Vergi Takvimi Widget (kullanıcının uygulama deneyimini önizler) */}
            <div className="lg:col-span-5">
              <HomeCalendarWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Mobil Uygulama Banner — App Store yayında duyurusu */}
      <section className="border-y border-gray-100 bg-gradient-to-r from-gray-900 via-navy-700 to-gray-900 py-10 lg:py-12 text-white">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col items-center gap-6 lg:flex-row lg:justify-between">
            <div className="text-center lg:text-left">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-green-500/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-green-300">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400"></span>
                App Store'da Yayında
              </div>
              <h2 className="text-2xl font-black sm:text-3xl">Mali Ajanda Mobil Uygulaması Yayında</h2>
              <p className="mt-2 text-sm text-gray-300 sm:text-base">
                {SITE_TAX_YEAR} vergi takvimi, cari hesap, hesaplama araçları ve hatırlatmalar — cebinizde. Ücretsiz indirin.
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-gray-400 lg:justify-start">
                <div className="flex items-center gap-1"><MdNotifications className="h-4 w-4" /> Bildirimler</div>
                <div className="flex items-center gap-1"><MdOfflineBolt className="h-4 w-4" /> Çevrimdışı</div>
                <div className="flex items-center gap-1"><MdCheck className="h-4 w-4 text-green-400" /> KVKK Uyumlu</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#fff' }}
                className="inline-flex items-center gap-3 rounded-xl bg-black px-5 py-3 shadow-lg ring-1 ring-white/20 transition hover:bg-gray-900"
                aria-label="App Store'dan İndir"
              >
                <FaApple style={{ color: '#fff' }} className="h-8 w-8" />
                <div className="flex flex-col leading-tight" style={{ color: '#fff' }}>
                  <span className="text-[10px] font-medium opacity-80">Şuradan İndirin:</span>
                  <span className="text-lg font-semibold">App Store</span>
                </div>
              </a>
              <Link to="/mobil-uygulama" className="text-sm font-medium text-gray-300 underline-offset-4 hover:text-white hover:underline">
                Detaylı Bilgi →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center">
            <h2 className="text-3xl font-black text-navy-700 sm:text-4xl">Her Şey Tek Platformda</h2>
            <p className="mt-3 text-gray-600">Mali müşavir olarak ihtiyacınız olan tüm araçlar</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: MdEvent, title: `${SITE_TAX_YEAR} Vergi Takvimi`, desc: `KDV, muhtasar (MUHSGK), e-Defter berat, geçici vergi ve daha fazlası. ${SITE_TAX_COUNT} vergi tarihi takip edilir, yaklaşan tarihler için uyarı.` },
              { icon: MdPeople, title: "Cari Hesap Takibi", desc: "Müşteri, tedarikçi, personel takibi. Borç/alacak muavin defteri, mizan raporu, PDF çıktı." },
              { icon: MdReceipt, title: "Çek / Senet Takibi", desc: "Alacak ve borç çek/senetlerinizi vade tarihine göre takip edin. Döviz cinsi desteği." },
              { icon: MdCalculate, title: "26 Hesaplama Aracı", desc: "KDV, kredi, kıdem tazminatı, damga vergisi, amortisman, maaş, stopaj, emeklilik ve daha fazlası." },
              { icon: MdTrendingUp, title: "Anlık Döviz & Altın", desc: "USD, EUR, GBP, CHF kurları ve gram, çeyrek, yarım, tam altın fiyatları anlık güncellenir." },
              { icon: MdVerifiedUser, title: "Güvenli ve Hızlı", desc: "Verileriniz SSL ile şifrelenir. Hızlı yükleme, responsive tasarım, her cihazda çalışır." },
            ].map((f, i) => (
              <div key={i} className="rounded-2xl border border-gray-100 p-6 transition hover:border-brand-200 hover:shadow-lg">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-navy-700">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vergi Takvimi Preview */}
      <section className="bg-gray-50 py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
            <div>
              <span className="inline-block rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-purple-600">{SITE_TAX_YEAR} TAKVİMİ</span>
              <h2 className="mt-3 text-3xl font-black text-navy-700">Hiçbir Beyanname Tarihini Kaçırmayın</h2>
              <p className="mt-4 text-gray-600">
                Mali Ajanda'nın {SITE_TAX_YEAR} vergi takvimi özelliği ile tüm beyanname ve bildirim tarihleri otomatik takip edilir.
                KDV, muhtasar (MUHSGK), e-Defter berat, geçici vergi, SGK primleri ve daha fazlası için yaklaşan tarihleri size bildirir.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><MdCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-500" /> Aylık/haftalık/günlük takip ekranı</li>
                <li className="flex items-start gap-2"><MdCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-500" /> Mini takvim ile görsel planlama</li>
                <li className="flex items-start gap-2"><MdCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-500" /> Yaklaşan 14 gün için acil uyarılar</li>
                <li className="flex items-start gap-2"><MdCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-500" /> Kendi özel hatırlatmalarınızı ekleyin</li>
              </ul>
              <Link to="/blog/2026-vergi-takvimi" className="mt-6 inline-block text-sm font-bold text-brand-500 hover:text-brand-600">
                2026 Vergi Takvimi Rehberini Oku →
              </Link>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
              <h4 className="mb-4 font-bold text-navy-700">Yaklaşan Vergi Tarihleri</h4>
              {yaklasanlar.length === 0 && (
                <p className="text-sm text-gray-500">Yaklaşan tarih bulunamadı. <Link to="/vergi-takvimi" className="font-semibold text-brand-500">Vergi takvimine git →</Link></p>
              )}
              {yaklasanlar.map((v, i) => (
                <div key={i} className="flex items-center justify-between border-b border-gray-100 py-2.5 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-navy-700">{v.title}</div>
                    <div className="text-xs text-gray-500">{v.date}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${v.color === "red" ? "bg-red-100 text-red-600" : v.color === "orange" ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"}`}>
                    {v.color === "red" ? "ACİL" : v.color === "orange" ? "Yakın" : "Normal"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mevzuat Haberleri */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-black text-navy-700 sm:text-4xl">Sektörden Güncel Haberler</h2>
            <p className="mt-3 text-gray-600">Mevzuat değişiklikleri, sirküler ve tebliğleri kaçırmayın</p>
          </div>
          <MevzuatHaberleriWidget limit={6} />
        </div>
      </section>

      {/* SEO İçerik */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-center text-3xl font-black text-navy-700">Mali Ajanda Nedir?</h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-gray-600">
            <p>
              <strong>Mali Ajanda</strong>, Türkiye'deki mali müşavirler (SMMM) ve işletmeler için geliştirilmiş dijital bir <strong>mali takvim</strong> ve yönetim platformudur. {SITE_TAX_YEAR} yılı <strong>vergi takvimi</strong> ile {SITE_TAX_COUNT}+ vergi tarihi otomatik takip edilir, yaklaşan beyannameler için uyarı gönderilir.
            </p>
            <p>
              KDV beyannamesi, muhtasar beyanname (MUHSGK), e-Defter berat, geçici vergi, SGK primleri, kurumlar vergisi ve emlak vergisi gibi tüm <strong>beyanname tarihlerini</strong> tek platformda takip edebilirsiniz. Ayrıca cari hesap takibi, çek ve senet yönetimi, muavin defter yazdırma ve <strong>26 profesyonel hesaplama aracı</strong> (KDV, kredi, kıdem tazminatı, stopaj, amortisman, damga vergisi, emeklilik, finansman gider kısıtlaması vb.) kullanımınıza sunulur.
            </p>
            <p>
              Mali müşavir yazılımları arasında <strong>Mali Ajanda</strong>, kullanımı kolay arayüzü, güncel {SITE_TAX_YEAR} vergi takvimi ve ücretsiz deneme imkanı ile öne çıkar. SMMM'ler için mizan raporu, toplu muavin yazdırma ve antetli kağıt desteği bulunmaktadır.
            </p>
          </div>
        </div>
      </section>

      {/* Popüler Aramalar / Internal Links */}
      <section className="bg-gray-50 py-16">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-center text-2xl font-black text-navy-700">Popüler Rehberler ve Araçlar</h2>
          <p className="mt-3 text-center text-gray-600">Mali müşavirler için en çok aranan konular</p>

          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-500">Vergi Takvimi</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><Link to="/blog/2026-vergi-takvimi" className="hover:text-brand-500">2026 Vergi Takvimi</Link></li>
                <li><Link to="/blog/nisan-2026-vergi-tarihleri" className="hover:text-brand-500">Nisan 2026 Vergi Tarihleri</Link></li>
                <li><Link to="/blog/mayis-2026-vergi-tarihleri" className="hover:text-brand-500">Mayıs 2026 Vergi Tarihleri</Link></li>
                <li><Link to="/blog/mali-takvim-nedir" className="hover:text-brand-500">Mali Takvim Nedir?</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-500">Beyannameler</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><Link to="/blog/kdv-beyannamesi" className="hover:text-brand-500">KDV Beyannamesi</Link></li>
                <li><Link to="/blog/muhtasar-beyanname" className="hover:text-brand-500">Muhtasar Beyanname</Link></li>
                <li><Link to="/blog/e-defter-berat" className="hover:text-brand-500">e-Defter Berat</Link></li>
                <li><Link to="/blog/gecici-vergi-2026" className="hover:text-brand-500">Geçici Vergi 2026</Link></li>
                <li><Link to="/blog/kurumlar-vergisi-beyannamesi-2026" className="hover:text-brand-500">Kurumlar Vergisi 2026</Link></li>
                <li><Link to="/blog/e-defter-berat" className="hover:text-brand-500">e-Defter ve Berat</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-500">Hesaplamalar</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><Link to="/blog/kdv-tevkifati" className="hover:text-brand-500">KDV Tevkifatı Nasıl Hesaplanır?</Link></li>
                <li><Link to="/blog/kidem-tazminati-2026" className="hover:text-brand-500">Kıdem Tazminatı 2026</Link></li>
                <li><Link to="/blog/sgk-prim-oranlari-2026" className="hover:text-brand-500">SGK Prim Oranları 2026</Link></li>
                <li><Link to="/blog/gelir-vergisi-tarifesi-2026" className="hover:text-brand-500">Gelir Vergisi Tarifesi 2026</Link></li>
                <li><Link to="/blog/cari-hesap-takibi" className="hover:text-brand-500">Cari Hesap Takibi</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-white p-6 border border-gray-200">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-500">En Çok Aranan Konular</h3>
            <div className="flex flex-wrap gap-2">
              {[
                `mali takvim ${SITE_TAX_YEAR}`, `vergi takvimi ${SITE_TAX_YEAR}`, "kdv beyannamesi ne zaman",
                "muhtasar beyanname tarihleri", "2026 geçici vergi dönemleri",
                "kurumlar vergisi 2026", "kdv tevkifatı oranları",
                "kıdem tazminatı hesaplama", "sgk prim oranları 2026",
                "gelir vergisi dilimleri 2026", "e-defter berat yükleme",
                "ücretsiz mali takvim", "online vergi takvimi", "smmm için cari takip",
              ].map((keyword, i) => (
                <span key={i} className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">{keyword}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-black text-navy-700 sm:text-4xl">Şimdi Başlayın, Ücretsiz</h2>
          <p className="mt-4 text-gray-600">Tamamen ücretsiz. Kredi kartı gerekmez. Hemen kullanmaya başlayın.</p>
          <Link to="/auth/sign-in" className="mt-8 inline-block rounded-xl bg-brand-500 px-10 py-4 text-base font-bold text-white shadow-lg shadow-brand-500/30 hover:bg-brand-600">
            Ücretsiz Hesap Oluştur
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Home;
